import { projectId, publicAnonKey } from '../utils/supabase/info';
import { isDevelopmentMode } from '../utils/environment';

export class FontService {
  private static instance: FontService;
  private fontsLoaded: Set<string> = new Set();
  private fontCache: Map<string, string> = new Map();

  static getInstance(): FontService {
    if (!FontService.instance) {
      FontService.instance = new FontService();
    }
    return FontService.instance;
  }

  // Check if fonts are already loaded locally
  async checkLocalFonts(): Promise<{ helloParis: boolean; sukar: boolean }> {
    if (typeof document === 'undefined') {
      return { helloParis: false, sukar: false };
    }

    try {
      // Try to detect if fonts are available locally
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        return { helloParis: false, sukar: false };
      }

      // Test Hello Paris font
      context.font = "20px 'Hello Paris', cursive";
      const helloParis = context.font.includes('Hello Paris');

      // Test Sukar font  
      context.font = "20px 'Sukar', sans-serif";
      const sukar = context.font.includes('Sukar');

      console.log('FontService: Local font detection -', { helloParis, sukar });
      
      return { helloParis, sukar };
    } catch (error) {
      console.warn('FontService: Error checking local fonts:', error);
      return { helloParis: false, sukar: false };
    }
  }

  // Load fonts from server or fallback to local fonts
  async loadBrandFonts(): Promise<void> {
    if (typeof document === 'undefined') {
      console.log('FontService: Document not available, skipping font loading');
      return;
    }

    console.log('FontService: Starting brand font loading...');

    // Check if we're in development mode first
    const isDevelopment = isDevelopmentMode();
    
    if (isDevelopment) {
      console.log('FontService: Development mode detected, using local font setup');
      await this.setupLocalBrandFonts();
      return;
    }

    // Check if we have valid server configuration
    const hasValidConfig = projectId && projectId !== 'your-project-id' && publicAnonKey;
    
    if (hasValidConfig) {
      try {
        await this.loadFontsFromServer();
        console.log('FontService: Successfully loaded fonts from server');
        return;
      } catch (error) {
        console.warn('FontService: Failed to load fonts from server, falling back to local detection:', error);
      }
    }

    // Fallback: Try to detect and use local fonts
    await this.setupLocalBrandFonts();
  }

  private async loadFontsFromServer(): Promise<void> {
    if (!projectId || !publicAnonKey) {
      throw new Error('Invalid server configuration');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-a35ebf46/fonts/css`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
          'Accept': 'text/css',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const css = await response.text();
        
        if (css && css.length > 100) {
          this.injectFontCSS(css, 'server-fonts');
          console.log('FontService: Injected server font CSS:', css.length, 'characters');
        } else {
          throw new Error('Empty or invalid CSS response');
        }
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  private async setupLocalBrandFonts(): Promise<void> {
    console.log('FontService: Setting up local brand fonts...');
    
    const localFonts = await this.checkLocalFonts();
    
    const brandFontCSS = `
/* Brand Fonts - Local Installation Priority */
@font-face {
  font-family: 'Hello Paris';
  src: local('Hello Paris'), local('HelloParis'), local('Hello-Paris'),
       local('Dancing Script'), local('Brush Script MT');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Hello Paris';
  src: local('Hello Paris Bold'), local('HelloParis-Bold'), local('Hello-Paris-Bold'),
       local('Dancing Script Bold'), local('Brush Script MT Bold');
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Sukar';
  src: local('Sukar'), local('Sukar-Regular'), local('Sukar Regular'),
       local('Inter'), local('Roboto'), local('system-ui');
  font-weight: 300;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Sukar';
  src: local('Sukar'), local('Sukar-Regular'), local('Sukar Regular'),
       local('Inter'), local('Roboto'), local('system-ui');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Sukar';
  src: local('Sukar Medium'), local('Sukar-Medium'), local('SukarMedium'),
       local('Inter Medium'), local('Roboto Medium');
  font-weight: 500;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Sukar';
  src: local('Sukar SemiBold'), local('Sukar-SemiBold'), local('SukarSemiBold'),
       local('Inter SemiBold'), local('Roboto Medium');
  font-weight: 600;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Sukar';
  src: local('Sukar Bold'), local('Sukar-Bold'), local('SukarBold'),
       local('Inter Bold'), local('Roboto Bold');
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}

/* Apply brand fonts with high specificity */
.font-hello-paris,
.font-logo,
.itinlux-logo,
.logo,
.site-logo,
.brand,
[data-logo="true"] {
  font-family: 'Hello Paris', 'Dancing Script', cursive !important;
  font-weight: 400 !important;
}

.font-sukar,
.font-body,
.font-headings,
body,
p, h1, h2, h3, h4, h5, h6,
button, input, textarea, label,
[data-body="true"],
[data-heading="true"] {
  font-family: 'Sukar', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif !important;
}

/* CSS Variables */
:root {
  --font-logo: 'Hello Paris', 'Dancing Script', cursive;
  --font-body: 'Sukar', 'Inter', system-ui, sans-serif;
  --font-headings: 'Sukar', 'Inter', system-ui, sans-serif;
}
`;

    this.injectFontCSS(brandFontCSS, 'local-brand-fonts');
    
    // Try to force font loading
    this.forceFontLoad(['Hello Paris', 'Sukar']);
    
    console.log('FontService: Local brand fonts setup complete');
  }

  private injectFontCSS(css: string, id: string): void {
    // Remove existing font styles with the same ID
    const existing = document.querySelector(`style[data-font-service="${id}"]`);
    if (existing) {
      existing.remove();
    }

    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-font-service', id);
    styleElement.setAttribute('type', 'text/css');
    styleElement.textContent = css;

    if (document.head) {
      document.head.appendChild(styleElement);
    }
  }

  private forceFontLoad(fontFamilies: string[]): void {
    try {
      fontFamilies.forEach(family => {
        if (document.fonts && document.fonts.load) {
          // Try to force font loading
          document.fonts.load(`400 16px "${family}"`).catch(error => {
            console.log(`FontService: Could not force load ${family}:`, error.message);
          });
          
          document.fonts.load(`700 16px "${family}"`).catch(error => {
            console.log(`FontService: Could not force load ${family} bold:`, error.message);
          });
        }
      });

      // Force style recalculation after a brief delay
      setTimeout(() => {
        const elements = document.querySelectorAll('.font-hello-paris, .font-sukar, [data-logo="true"], [data-body="true"]');
        elements.forEach(el => {
          if (el instanceof HTMLElement) {
            // Force repaint
            el.style.transform = 'translateZ(0)';
            el.offsetHeight; // Trigger reflow
            el.style.transform = '';
          }
        });
      }, 100);
    } catch (error) {
      console.warn('FontService: Error forcing font load:', error);
    }
  }

  // Refresh font loading
  async refreshFonts(): Promise<void> {
    console.log('FontService: Refreshing fonts...');
    this.fontsLoaded.clear();
    this.fontCache.clear();
    await this.loadBrandFonts();
  }

  // Check if specific font is loaded
  isFontLoaded(fontFamily: string): boolean {
    return this.fontsLoaded.has(fontFamily);
  }

  // Get available fonts
  getAvailableFonts(): string[] {
    return Array.from(this.fontsLoaded);
  }
}

// Export singleton instance
export const fontService = FontService.getInstance();